/**
 * Get Meter Values Use Case
 * Business logic for fetching and exporting meter values for a session
 */

import { SessionsReportRepository } from '../data/sessions-report-repository';
import {
  MeterValueDetailApiModel,
  MeterValueGridRow,
  MeterValuesRequest,
  getReadingContextLabel,
} from '../types/meter-value-types';
import { formatDateTime } from '../utils/date-utils';

export interface GetMeterValuesResult {
  meterValues: MeterValueGridRow[];
  totalCount: number;
}

export class GetMeterValuesUseCase {
  constructor(private readonly repository: SessionsReportRepository) {}

  /**
   * Transform API model to grid row
   */
  private transformToGridRow(item: MeterValueDetailApiModel): MeterValueGridRow {
    return {
      meterValueId: item.meterValueId ?? 0,
      sessionId: item.sessionId ?? 0,
      timestamp: item.timeStamp ? formatDateTime(item.timeStamp) : '--',
      machineTimestamp: item.machineTimeStamp ? formatDateTime(item.machineTimeStamp) : '--',
      readingContext: getReadingContextLabel(item.readingContexts),
      energy: item.energy,
      deltaEnergy: item.deltaEnergy,
      power: item.power,
      current: item.current,
      voltage: item.voltage,
      temperature: item.temperature,
      stateOfCharge: item.stateOfCharge,
      tariffName: item.taarifName,
      unitCost: item.unitCost,
      costExclVat: item.cost,
      vatCost: item.vatCost,
      costInclVat: item.totalCost,
      vatRate: item.vatRate,
      isVirtualMeter: item.isVirtualMeter ?? false,
    };
  }

  /**
   * Execute: Get paginated list of meter values for a session
   */
  async execute(
    sessionId: number,
    page: number = 0,
    pageSize: number = 100,
    orderBy: string = 'timeStamp desc',
    filterEmptyDelta: boolean = false
  ): Promise<GetMeterValuesResult> {
    try {
      const params: MeterValuesRequest = {
        $skip: page * pageSize,
        $top: pageSize,
        $orderby: orderBy,
        timeZoneOffset: new Date().getTimezoneOffset() * -1, // Convert to positive offset
        filterEmptyDelta, // Hide rows with Delta Energy = 0 when true
      };

      // Make both requests in parallel for better performance
      const [response, totalCount] = await Promise.all([
        this.repository.getMeterValuesDetails(sessionId, params),
        this.repository.getMeterValuesCount(sessionId, params),
      ]);

      const dataArray = response.value || (response as unknown as MeterValueDetailApiModel[]);

      const meterValues = (Array.isArray(dataArray) ? dataArray : []).map((item) =>
        this.transformToGridRow(item)
      );

      return {
        meterValues,
        totalCount,
      };
    } catch (error) {
      console.error('GetMeterValuesUseCase.execute failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to load meter values. Please try again.'
      );
    }
  }

  /**
   * Export meter values to Excel
   * Returns a blob that can be downloaded
   */
  async exportToExcel(
    sessionId: number,
    totalCount: number,
    addVat?: boolean,
    addCost?: boolean,
    addBuyCost?: boolean,
    addSellCost?: boolean
  ): Promise<void> {
    try {
      const params: MeterValuesRequest = {
        $top: totalCount, // Get all records for export
        $skip: 0,
        $orderby: 'timeStamp desc',
        timeZoneOffset: new Date().getTimezoneOffset() * -1,
        addVat,
        addCost,
        addBuyCost,
        addSellCost,
      };

      const blob = await this.repository.exportMeterValuesToExcel(sessionId, params);

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `session-${sessionId}-meter-values.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('GetMeterValuesUseCase.exportToExcel failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to export meter values. Please try again.'
      );
    }
  }
}
